import { useState, useCallback, useRef, useEffect } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Location } from "../data/types.js";
import { areas } from "../data/areas.js";

type AreaId = "sensoji" | "sumida-park" | "west-asakusa";

const AREA_OPTIONS: Array<{ id: AreaId; label: string }> = [
  { id: "sensoji", label: "Asakusa" },
  { id: "sumida-park", label: "Sumida" },
  { id: "west-asakusa", label: "West" },
];

interface PinData {
  id: string;
  number: number;
  name: string;
  nameJa: string;
  /**
   * Kept on the record so existing KV data isn't discarded, but no
   * longer edited from the admin UI (photo tip covers the user-facing
   * copy now).
   */
  description: string;
  x: number;
  y: number;
  mapsUrl: string;
  tip: string;
  allowNight: boolean;
  allowSakura: boolean;
  allowMomiji: boolean;
  allowHot: boolean;
  /** Admin-defined display order for Cloudinary photos (URL list) */
  photoOrder: string[];
  /** Cloudinary URLs the admin has chosen to hide */
  hiddenPhotos: string[];
}

const EMPTY_PIN: PinData = {
  id: "",
  number: 1,
  name: "",
  nameJa: "",
  description: "",
  x: 50,
  y: 50,
  mapsUrl: "",
  tip: "",
  allowNight: false,
  allowSakura: false,
  allowMomiji: false,
  allowHot: false,
  photoOrder: [],
  hiddenPhotos: [],
};

export function PinEditor() {
  const [area, setArea] = useState<AreaId>("sensoji");
  const [pins, setPins] = useState<PinData[]>([]);
  const [selectedPin, setSelectedPin] = useState<number | null>(null);
  const [isPlacing, setIsPlacing] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"" | "saving" | "saved" | "error">("");
  const [syncStatus, setSyncStatus] = useState<"" | "syncing" | "synced" | "error">("");
  const [syncInfo, setSyncInfo] = useState("");
  const [cloudPhotos, setCloudPhotos] = useState<Record<string, string[]>>({});
  const imageRef = useRef<HTMLImageElement>(null);

  const currentArea = areas.find((a) => a.id === area) ?? areas[1];
  const lsKey = `kra-pin-editor:${area}`;

  // Load pins + cloudinary photos whenever the selected area changes.
  // Old KV records won't have the photoOrder/hiddenPhotos fields —
  // normalize so every pin round-trips without producing `undefined` reads.
  useEffect(() => {
    const normalize = (raw: unknown[]): PinData[] =>
      raw.map((r) => ({
        ...EMPTY_PIN,
        ...(r as Partial<PinData>),
        photoOrder: (r as Partial<PinData>).photoOrder ?? [],
        hiddenPhotos: (r as Partial<PinData>).hiddenPhotos ?? [],
      }));

    setSelectedPin(null);
    setCloudPhotos({});

    fetch(`/api/pins?area=${area}`)
      .then((r) => r.json())
      .then((data: unknown[]) => {
        if (Array.isArray(data) && data.length > 0) {
          setPins(normalize(data));
        } else {
          const saved = localStorage.getItem(lsKey);
          setPins(saved ? normalize(JSON.parse(saved)) : []);
        }
      })
      .catch(() => {
        const saved = localStorage.getItem(lsKey);
        setPins(saved ? normalize(JSON.parse(saved)) : []);
      });

    fetch(`/api/photos?area=${area}`)
      .then((r) => r.json())
      .then((data) => {
        if (data && !data.error) setCloudPhotos(data);
      })
      .catch(() => {});
  }, [area, lsKey]);

  /** Sync photos from Cloudinary → KV for the current area */
  const syncPhotos = useCallback(async () => {
    setSyncStatus("syncing");
    setSyncInfo("");
    try {
      const res = await fetch(`/api/photos?admin=1&area=${area}`, {
        method: "POST",
      });
      const data = await res.json();
      if (data.ok) {
        setSyncStatus("synced");
        setSyncInfo(`${data.photos} photos in ${data.folders} folders`);
        const photosRes = await fetch(`/api/photos?area=${area}`);
        const photos = await photosRes.json();
        if (photos && !photos.error) setCloudPhotos(photos);
        setTimeout(() => setSyncStatus(""), 3000);
      } else {
        setSyncStatus("error");
        setSyncInfo(data.error || "Unknown error");
      }
    } catch {
      setSyncStatus("error");
      setSyncInfo("Network error");
    }
  }, [area]);

  const save = useCallback(
    (updated: PinData[]) => {
      setPins(updated);
      localStorage.setItem(lsKey, JSON.stringify(updated));
    },
    [lsKey],
  );

  /** Publish pins to server (Cloudflare KV) for the current area */
  const publishToServer = useCallback(async () => {
    setSaveStatus("saving");
    try {
      const res = await fetch(`/api/pins?admin=1&area=${area}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pins),
      });
      if (res.ok) {
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } else {
        setSaveStatus("error");
      }
    } catch {
      setSaveStatus("error");
    }
  }, [pins, area]);

  const handleMapClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPlacing || !imageRef.current) return;
      const rect = imageRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      const newPin: PinData = {
        ...EMPTY_PIN,
        id: `pin-${Date.now()}`,
        number: pins.length + 1,
        x: Math.round(x * 10) / 10,
        y: Math.round(y * 10) / 10,
      };
      const updated = [...pins, newPin];
      save(updated);
      setSelectedPin(updated.length - 1);
      setIsPlacing(false);
    },
    [isPlacing, pins, save]
  );

  const updatePin = useCallback(
    <K extends keyof PinData>(index: number, field: K, value: PinData[K]) => {
      const updated = [...pins];
      updated[index] = { ...updated[index], [field]: value };
      save(updated);
    },
    [pins, save]
  );

  const deletePin = useCallback(
    (index: number) => {
      const updated = pins.filter((_, i) => i !== index);
      // Re-number
      updated.forEach((p, i) => (p.number = i + 1));
      save(updated);
      setSelectedPin(null);
    },
    [pins, save]
  );

  const handlePinDrag = useCallback(
    (index: number, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!imageRef.current) return;
      const img = imageRef.current;

      const onMove = (ev: MouseEvent) => {
        const rect = img.getBoundingClientRect();
        const x = ((ev.clientX - rect.left) / rect.width) * 100;
        const y = ((ev.clientY - rect.top) / rect.height) * 100;
        const updated = [...pins];
        updated[index] = {
          ...updated[index],
          x: Math.round(Math.max(0, Math.min(100, x)) * 10) / 10,
          y: Math.round(Math.max(0, Math.min(100, y)) * 10) / 10,
        };
        setPins(updated);
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        localStorage.setItem("kra-pin-editor", JSON.stringify(pins));
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [pins]
  );

  const exportJson = useCallback(() => {
    const output: Partial<Location>[] = pins.map((p) => ({
      id: p.id,
      number: p.number,
      name: p.name || `Spot ${p.number}`,
      nameJa: p.nameJa || "",
      description: p.description || "",
      x: p.x,
      y: p.y,
      mapsUrl: p.mapsUrl || "",
      photos: [],
      tip: p.tip || undefined,
      allowNight: p.allowNight || undefined,
      allowSakura: p.allowSakura || undefined,
      allowMomiji: p.allowMomiji || undefined,
      allowHot: p.allowHot || undefined,
      photoOrder: p.photoOrder.length > 0 ? p.photoOrder : undefined,
      hiddenPhotos: p.hiddenPhotos.length > 0 ? p.hiddenPhotos : undefined,
    }));
    return JSON.stringify(output, null, 2);
  }, [pins]);

  return (
    <div className="fixed inset-0 flex bg-surface-0">
      {/* Map area — sizes to the image width so the sidebar can eat the
          remaining horizontal space (no more dead zone in the middle). */}
      <div
        className="shrink-0 relative overflow-auto bg-surface-2"
        onClick={handleMapClick}
        style={{ cursor: isPlacing ? "crosshair" : "default" }}
      >
        <div className="relative inline-block">
          <img
            ref={imageRef}
            src={currentArea.mapImage}
            alt="Map"
            className="block max-h-screen"
            draggable={false}
          />

          {/* Pins */}
          {pins.map((pin, i) => (
            <div
              key={pin.id}
              className={`absolute flex flex-col items-center cursor-grab active:cursor-grabbing`}
              style={{
                left: `${pin.x}%`,
                top: `${pin.y}%`,
                transform: "translate(-50%, -100%)",
                zIndex: selectedPin === i ? 50 : 10,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedPin(i);
              }}
              onMouseDown={(e) => handlePinDrag(i, e)}
            >
              <div
                className={`flex items-center justify-center rounded-full text-xs font-bold transition-all ${
                  selectedPin === i
                    ? "w-10 h-10 bg-kra text-white shadow-lg ring-2 ring-white"
                    : "w-8 h-8 bg-white text-kra shadow-md ring-2 ring-kra"
                }`}
              >
                {pin.number}
              </div>
              <div className="w-0.5 h-1.5 bg-kra" />
              <div className="text-[9px] mt-0.5 bg-white/90 px-1 rounded text-ink-1 whitespace-nowrap">
                {pin.name || `Spot ${pin.number}`}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Sidebar — fills the entire remaining width to the right of the
          map so the photo grid has room to breathe on wide monitors. */}
      <div className="flex-1 min-w-[360px] bg-white border-l border-surface-2 flex flex-col overflow-hidden">
        {/* Toolbar — area tabs + two rows of buttons, calm brand palette.
            Row 0: Area switcher (Asakusa / Sumida / West)
            Row 1: Add Pin (primary secondary action) + JSON (utility).
            Row 2: Save & Publish (primary) + Sync Photos (outline). */}
        <div className="px-4 pt-4 pb-3 border-b border-surface-2 space-y-2.5">
          <div className="flex gap-1 p-1 bg-surface-1 rounded-lg">
            {AREA_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setArea(opt.id)}
                className={`flex-1 h-8 rounded-md text-xs font-medium transition-colors ${
                  area === opt.id
                    ? "bg-white text-ink-0 shadow-sm"
                    : "text-ink-2 hover:text-ink-0"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setIsPlacing(!isPlacing)}
              className={`flex-1 h-9 rounded-lg text-xs font-medium transition-colors ${
                isPlacing
                  ? "bg-kra text-white"
                  : "bg-surface-1 text-ink-1 hover:bg-surface-2"
              }`}
            >
              {isPlacing ? "Click map to place…" : "+ Add Pin"}
            </button>
            <button
              onClick={() => setShowExport(!showExport)}
              className={`h-9 px-3.5 rounded-lg text-[11px] font-semibold tracking-wide transition-colors ${
                showExport
                  ? "bg-ink-0 text-white"
                  : "bg-surface-1 text-ink-2 hover:bg-surface-2"
              }`}
            >
              JSON
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={publishToServer}
              disabled={saveStatus === "saving"}
              className={`flex-1 h-10 rounded-lg text-xs font-semibold transition-all active:scale-[0.985] ${
                saveStatus === "saved"
                  ? "bg-emerald-600 text-white"
                  : saveStatus === "error"
                  ? "bg-red-500 text-white"
                  : "bg-kra text-white hover:bg-kra-dark shadow-sm shadow-kra/20"
              }`}
            >
              {saveStatus === "saving"
                ? "Saving…"
                : saveStatus === "saved"
                ? "Saved"
                : saveStatus === "error"
                ? "Error — retry"
                : "Save & Publish"}
            </button>
            <button
              onClick={syncPhotos}
              disabled={syncStatus === "syncing"}
              title={syncInfo || "Re-read Cloudinary → KV"}
              className={`flex-1 h-10 rounded-lg text-xs font-medium transition-all active:scale-[0.985] ${
                syncStatus === "synced"
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : syncStatus === "error"
                  ? "bg-red-50 text-red-700 border border-red-200"
                  : "bg-white text-ink-0 border border-ink-3/25 hover:border-kra hover:text-kra"
              }`}
            >
              {syncStatus === "syncing"
                ? "Syncing…"
                : syncStatus === "synced"
                ? `Synced · ${syncInfo}`
                : syncStatus === "error"
                ? "Sync failed"
                : "Sync Photos"}
            </button>
          </div>
        </div>

        {/* Below toolbar: pin list (persistent left rail) + edit form.
            JSON export mode takes the full width. */}
        <div className="flex-1 flex min-h-0">
          {showExport ? (
            <div className="flex-1 overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-ink-1">JSON Export</span>
                <button
                  onClick={() => navigator.clipboard.writeText(exportJson())}
                  className="text-xs text-kra font-medium hover:text-kra-dark"
                >
                  Copy
                </button>
              </div>
              <textarea
                value={exportJson()}
                readOnly
                className="w-full h-[70vh] text-[10px] font-mono bg-surface-1 rounded-lg p-2 text-ink-1 resize-none"
              />
            </div>
          ) : (
            <>
              {/* Edit form / empty state (left side — photo grid gets the
                  wide real estate; the pin list lives on the right now). */}
              <div className="flex-1 overflow-y-auto min-w-0">
                {selectedPin !== null && pins[selectedPin] ? (
                  <div className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-ink-0">
                        Pin #{pins[selectedPin].number}
                      </span>
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] text-ink-2 font-mono">
                          x:{pins[selectedPin].x}% y:{pins[selectedPin].y}%
                        </span>
                        <button
                          onClick={() => deletePin(selectedPin)}
                          className="text-xs text-red-500 hover:text-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

              {/* Season/lighting flags — same set the public viewer filters on */}
              <div>
                <label className="text-[10px] text-ink-2 uppercase tracking-wider">
                  Seasons
                </label>
                <div className="flex flex-wrap gap-3 mt-1.5">
                  {(
                    [
                      ["allowNight", "🌙 Night"],
                      ["allowSakura", "🌸 Sakura"],
                      ["allowMomiji", "🍁 Momiji"],
                      ["allowHot", "🔥 Hot"],
                    ] as const
                  ).map(([field, label]) => (
                    <label
                      key={field}
                      className="flex items-center gap-1.5 cursor-pointer px-2.5 py-1 rounded-full bg-surface-1 hover:bg-surface-2 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={pins[selectedPin][field] ?? false}
                        onChange={(e) =>
                          updatePin(selectedPin, field, e.target.checked)
                        }
                        className="w-3.5 h-3.5 rounded accent-kra"
                      />
                      <span className="text-xs text-ink-0 whitespace-nowrap">
                        {label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Name EN + JP side-by-side */}
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    ["name", "Name (EN)"],
                    ["nameJa", "Name (JP)"],
                  ] as const
                ).map(([field, label]) => (
                  <div key={field}>
                    <label className="text-[10px] text-ink-2 uppercase tracking-wider">
                      {label}
                    </label>
                    <input
                      value={pins[selectedPin][field] as string}
                      onChange={(e) =>
                        updatePin(selectedPin, field, e.target.value)
                      }
                      className="w-full mt-1 px-2 py-1.5 text-xs bg-surface-1 rounded-lg text-ink-0"
                    />
                  </div>
                ))}
              </div>

              <div>
                <label className="text-[10px] text-ink-2 uppercase tracking-wider">
                  Google Maps URL
                </label>
                <input
                  value={pins[selectedPin].mapsUrl}
                  onChange={(e) =>
                    updatePin(selectedPin, "mapsUrl", e.target.value)
                  }
                  className="w-full mt-1 px-2 py-1.5 text-xs bg-surface-1 rounded-lg text-ink-0"
                />
              </div>

              {/* Photo tip — textarea so tips can wrap across lines.
                  LocationSheet already splits on \n when rendering, so
                  each line becomes its own 📷 bullet in the viewer. */}
              <div>
                <label className="text-[10px] text-ink-2 uppercase tracking-wider">
                  Photo tip
                </label>
                <textarea
                  value={pins[selectedPin].tip}
                  onChange={(e) =>
                    updatePin(selectedPin, "tip", e.target.value)
                  }
                  rows={4}
                  placeholder="Mẹo chụp ở pin này (Enter để xuống dòng — mỗi dòng là 1 bullet trên viewer)"
                  className="w-full mt-1 px-2 py-1.5 text-xs bg-surface-1 rounded-lg text-ink-0 resize-y leading-relaxed placeholder:text-ink-3"
                />
              </div>

              {/* Cloudinary photos — drag to reorder, × to hide.
                  The viewer applies `photoOrder` first, then drops any
                  URL in `hiddenPhotos`, so deleting here never touches
                  Cloudinary itself. */}
                    <PhotoManager
                      pin={pins[selectedPin]}
                      cloudUrls={
                        cloudPhotos[String(pins[selectedPin].number)] || []
                      }
                      onReorder={(order) =>
                        updatePin(selectedPin, "photoOrder", order)
                      }
                      onHide={(url) => {
                        const current = pins[selectedPin].hiddenPhotos;
                        updatePin(selectedPin, "hiddenPhotos", [
                          ...current,
                          url,
                        ]);
                      }}
                      onRestore={(url) => {
                        const current = pins[selectedPin].hiddenPhotos;
                        updatePin(
                          selectedPin,
                          "hiddenPhotos",
                          current.filter((u) => u !== url)
                        );
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center p-8 text-center">
                    <div>
                      <div className="text-3xl mb-2">📍</div>
                      <p className="text-xs text-ink-2">
                        {pins.length > 0
                          ? "Select a pin on the right to edit"
                          : "Click + Add Pin, then click on the map"}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Persistent pin list — right rail */}
              <div className="w-44 shrink-0 border-l border-surface-2 overflow-y-auto py-2 bg-surface-0/50">
                <p className="px-3 pb-1.5 text-[10px] uppercase tracking-wider text-ink-2">
                  {pins.length} pins
                </p>
                {pins.map((pin, i) => (
                  <button
                    key={pin.id}
                    onClick={() => setSelectedPin(i)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
                      selectedPin === i
                        ? "bg-kra/10 text-ink-0"
                        : "hover:bg-surface-1 text-ink-1"
                    }`}
                  >
                    <span
                      className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold shrink-0 ${
                        selectedPin === i
                          ? "bg-kra text-white"
                          : "bg-surface-2 text-ink-1"
                      }`}
                    >
                      {pin.number}
                    </span>
                    <span className="text-xs truncate">
                      {pin.name || `Spot ${pin.number}`}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Photo manager ─── */

interface PhotoManagerProps {
  pin: PinData;
  cloudUrls: string[];
  onReorder: (order: string[]) => void;
  onHide: (url: string) => void;
  onRestore: (url: string) => void;
}

/**
 * Visual editor for one pin's Cloudinary photos.
 *
 * - Visible photos are rendered in a draggable vertical list (framer-motion
 *   `Reorder`). Dropping writes a new `photoOrder` for the pin.
 * - Each photo has an × button that appends its URL to `hiddenPhotos`
 *   (it's not deleted from Cloudinary, just hidden from the viewer).
 * - Hidden photos are collapsed below with a "Restore" link so mistakes
 *   are recoverable.
 */
function PhotoManager({
  pin,
  cloudUrls,
  onReorder,
  onHide,
  onRestore,
}: PhotoManagerProps) {
  const hidden = new Set(pin.hiddenPhotos);
  const visible = cloudUrls.filter((u) => !hidden.has(u));

  // Sort visible URLs by photoOrder (URLs not in order list go last).
  const ordered = pin.photoOrder.length
    ? [...visible].sort((a, b) => {
        const ia = pin.photoOrder.indexOf(a);
        const ib = pin.photoOrder.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      })
    : visible;

  const hiddenList = cloudUrls.filter((u) => hidden.has(u));

  if (cloudUrls.length === 0) {
    return (
      <div>
        <label className="text-[10px] text-ink-2 uppercase tracking-wider">
          Photos
        </label>
        <p className="text-[10px] text-ink-2 mt-1">
          No photos yet — upload to kra-asakusa/{pin.number}/ on Cloudinary.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-[10px] text-ink-2 uppercase tracking-wider">
          Photos ({ordered.length} visible, {hiddenList.length} hidden)
        </label>
        <span className="text-[10px] text-ink-2 italic">
          drag to reorder
        </span>
      </div>

      {/* @dnd-kit/sortable handles 2D grid reordering properly —
          framer-motion's Reorder only supports 1D axes and was producing
          visual gaps on drag in this grid. */}
      <SortablePhotoGrid
        items={ordered}
        onReorder={onReorder}
        onHide={onHide}
      />

      {hiddenList.length > 0 && (
        <div className="mt-4 pt-3 border-t border-surface-2">
          <label className="text-[10px] text-ink-2 uppercase tracking-wider">
            Hidden ({hiddenList.length})
          </label>
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-1.5 mt-1.5">
            {hiddenList.map((url) => (
              <button
                key={url}
                type="button"
                onClick={() => onRestore(url)}
                className="relative aspect-square rounded-md overflow-hidden group"
                title="Click to restore"
              >
                <img
                  src={url}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-100 transition-opacity"
                />
                <span className="absolute inset-0 flex items-center justify-center text-[9px] font-medium text-white bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity">
                  restore
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Sortable photo grid (dnd-kit) ─── */

interface SortablePhotoGridProps {
  items: string[];
  onReorder: (next: string[]) => void;
  onHide: (url: string) => void;
}

function SortablePhotoGrid({
  items,
  onReorder,
  onHide,
}: SortablePhotoGridProps) {
  // Require ~6 px of movement before a drag starts so clicks on the ×
  // button aren't mistaken for drag gestures.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.indexOf(String(active.id));
    const newIndex = items.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items} strategy={rectSortingStrategy}>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {items.map((url, i) => (
            <SortablePhoto
              key={url}
              url={url}
              index={i}
              onHide={() => onHide(url)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

interface SortablePhotoProps {
  url: string;
  index: number;
  onHide: () => void;
}

function SortablePhoto({ url, index, onHide }: SortablePhotoProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: url });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square rounded-lg overflow-hidden bg-surface-1 select-none group ${
        isDragging
          ? "cursor-grabbing shadow-[0_12px_28px_rgba(0,0,0,0.22)] scale-[1.03]"
          : "cursor-grab"
      }`}
      {...attributes}
      {...listeners}
    >
      <img
        src={url}
        alt=""
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
      />
      <span className="absolute top-1.5 left-1.5 flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-black/55 text-white text-[11px] font-semibold backdrop-blur-sm">
        {index + 1}
      </span>
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onHide}
        className="absolute top-1.5 right-1.5 w-[22px] h-[22px] rounded-full bg-black/55 hover:bg-red-600 text-white flex items-center justify-center backdrop-blur-sm transition-colors text-sm leading-none"
        aria-label="Hide photo"
        title="Hide photo"
      >
        ×
      </button>
    </div>
  );
}
